import { ChangeEvent, useEffect, useState } from "react";

import {
	closestCenter,
	DndContext,
	DragEndEvent,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	rectSortingStrategy,
	SortableContext,
} from "@dnd-kit/sortable";
import { Button, Typography } from "@mui/material";
import classNames from "classnames";
import { GoAlert } from "react-icons/go";
import { IoSync } from "react-icons/io5";
import * as uuid from "uuid";

import { TCourseDocument } from "../../../../../@types";
import { Sortable } from "../../../../common/DragAndDrop/SortableItem";
import {
	TLocalCourseDocument,
	useMyCourseInstructor,
} from "../../hooks/useMyCourseInstructor";
import { CloudCourseDocumentImage } from "./CloudCourseDocumentImage";
import { CloudCourseDocumentVideo } from "./CloudCourseDocumentVideo";
import { LocalCourseDocumentImage } from "./LocalCourseDocumentImage";
import { LocalCourseDocumentVideo } from "./LocalCourseDocumentVideo";

export function MyCourseMedia() {
	const { courseDocuments, updateCourseDocuments } = useMyCourseInstructor();
	const [mediaFiles, setMediaFiles] =
		useState<(TCourseDocument | TLocalCourseDocument)[]>(courseDocuments);
	const [loading, setLoading] = useState(false);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: { distance: 10 },
		}),
	);

	useEffect(() => {
		setMediaFiles(courseDocuments);
	}, [courseDocuments]);

	const onSelectFiles = (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (!files) {
			return;
		}
		for (let i = 0; i < files.length && mediaFiles.length + i < 9; i++) {
			const file = files.item(i);
			if (!file) {
				return;
			}
			const reader = new FileReader();
			reader.onloadend = function () {
				const id = uuid.v4();
				setMediaFiles((prevMediaFiles) => [
					...prevMediaFiles,
					{
						type: "local",
						fileType:
							(file.type.split("/")[0] as TLocalCourseDocument["fileType"]) ??
							"image",
						data: reader.result as string,
						id,
						file,
					},
				]);
			};
			reader.readAsDataURL(file);
		}
	};

	const onRemoveFile = (id: string | number) => {
		setMediaFiles((prevMediaFiles) =>
			prevMediaFiles.filter((mediaFile) => mediaFile.id !== id),
		);
	};

	const onClickCancel = () => {
		setMediaFiles(courseDocuments);
	};

	const onClickUpdate = async () => {
		setLoading(true);
		await updateCourseDocuments(mediaFiles);
		setLoading(false);
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (over && active.id !== over.id) {
			setMediaFiles((items) => {
				const oldIndex = items.findIndex(
					(mediaFile) => mediaFile.id === active.id,
				);
				const newIndex = items.findIndex(
					(mediaFile) => mediaFile.id === over.id,
				);

				return arrayMove(items, oldIndex, newIndex);
			});
		}
	};

	const isEdited =
		mediaFiles.map((mediaFile) => mediaFile.id).join(",") !==
			courseDocuments.map((courseDocument) => courseDocument.id).join(",") &&
		!loading;
	const isDisabled = mediaFiles.length === 0 || !isEdited || loading;

	return (
		<>
			<div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
				<Typography className="small-title-text bold">
					3. Paint the picture!
				</Typography>
				{courseDocuments.length === 0 && (
					<div>
						<GoAlert className="normal-icon warning-icon" />
					</div>
				)}
				{loading && (
					<div>
						<IoSync className="normal-icon rotation-icon blue-icon" />
					</div>
				)}
			</div>
			<Typography className="normal-text">
				Show the learning experience at play. You'll need at least 1 media file
				to get started.
			</Typography>
			{mediaFiles.length > 0 && (
				<div className="course-media-files-container item-separator-small">
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}>
						<SortableContext items={mediaFiles} strategy={rectSortingStrategy}>
							{mediaFiles.map((mediaFile, index) => {
								if (mediaFile.type === "local") {
									if (mediaFile.fileType === "image") {
										return (
											<Sortable key={mediaFile.id} id={mediaFile.id}>
												<LocalCourseDocumentImage
													file={mediaFile}
													onRemoveFile={onRemoveFile}
												/>
											</Sortable>
										);
									}

									if (mediaFile.fileType === "video") {
										return (
											<Sortable key={mediaFile.id} id={mediaFile.id}>
												<LocalCourseDocumentVideo
													file={mediaFile}
													key={mediaFile.id}
													onRemoveFile={onRemoveFile}
												/>
											</Sortable>
										);
									}

									return null;
								}

								if (mediaFile.contentType?.startsWith("image")) {
									return (
										<Sortable key={mediaFile.id} id={mediaFile.id}>
											<CloudCourseDocumentImage
												file={mediaFile}
												key={mediaFile.id}
												onRemoveFile={onRemoveFile}
											/>
										</Sortable>
									);
								}
								if (mediaFile.contentType?.startsWith("video")) {
									return (
										<Sortable key={mediaFile.id} id={mediaFile.id}>
											<CloudCourseDocumentVideo
												file={mediaFile}
												key={mediaFile.id}
												onRemoveFile={onRemoveFile}
											/>
										</Sortable>
									);
								}

								return null;
							})}
						</SortableContext>
					</DndContext>
				</div>
			)}
			{mediaFiles.length < 9 && (
				<div className="file-input-container item-separator-small">
					<input
						type="file"
						value=""
						accept="image/png,image/jpeg,video/mp4"
						className="file-input"
						onChange={onSelectFiles}
						multiple
					/>
					<div className="file-input-content-container">
						<Typography className="small-title-text bold" textAlign="center">
							Choose up to 9 files
						</Typography>
						<Typography
							className="normal-text item-separator-small"
							textAlign="center">
							Drag 'n' drop or click to select files. We support MP4, PNG,
							JPG/JPEG.
						</Typography>
					</div>
				</div>
			)}
			<div className="button-container item-separator-small">
				<Button
					className="normal-button disabled-button"
					disabled={!isEdited}
					onClick={onClickCancel}>
					Cancel
				</Button>
				<Button
					disabled={isDisabled}
					className={classNames("normal-button", {
						"primary-button": !isDisabled,
						"disabled-button": isDisabled,
					})}
					onClick={onClickUpdate}>
					Save
				</Button>
			</div>
		</>
	);
}
